/**
 * shape-adapters.ts — Sprint 63
 *
 * Pure functions that adapt admin-server response payloads (which sometimes
 * wrap arrays in { items, count } envelopes and sometimes return bare arrays)
 * into the array shapes the React pages consume.
 *
 * Kept dependency-free so they can be exercised by Node's built-in test runner
 * (see admin-ui/smoke/shape-adapters.smoke.mjs) without bundler / DOM setup.
 *
 * All adapters tolerate three shapes:
 *   1. bare array
 *   2. { <key>: array } envelope
 *   3. null/undefined → []
 *
 * Plus, for single-account search:
 *   4. single AccountDetail object → [obj]
 */

export function adaptShardHealthResponse<T>(
  result: T[] | { shards: T[]; count?: number } | null | undefined,
): T[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && 'shards' in result) {
    return (result as { shards: T[] }).shards ?? [];
  }
  return [];
}

export function adaptDeadLettersResponse<T>(
  result:
    | T[]
    | { dead_lettered_events: T[]; count?: number }
    | null
    | undefined,
): T[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && 'dead_lettered_events' in result) {
    return (result as { dead_lettered_events: T[] }).dead_lettered_events ?? [];
  }
  return [];
}

export function adaptAbuseSignalsResponse<T>(
  result: T[] | { signals: T[]; count?: number } | null | undefined,
): T[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && 'signals' in result) {
    return (result as { signals: T[] }).signals ?? [];
  }
  return [];
}

/**
 * Account email search returns three shapes depending on backend version:
 *   - single AccountDetail (current findAdminAccountByEmail)
 *   - array
 *   - { accounts: [...] }
 *
 * The discriminator for a single AccountDetail is the presence of account_id.
 */
export function adaptAccountSearchResponse<T extends { account_id?: string }>(
  result: T | T[] | { accounts: T[]; total?: number } | null | undefined,
): T[] {
  if (result == null) return [];
  if (Array.isArray(result)) return result;
  if (typeof result === 'object' && 'accounts' in result) {
    return (result as { accounts: T[] }).accounts ?? [];
  }
  if (typeof result === 'object' && 'account_id' in result) {
    return [result as T];
  }
  return [];
}
